import { useEffect, useState } from 'react'
import { decideProposal, fetchProposal, ProposalSettledError } from '@/services/integrations'
import { mergeProposalOutcome, proposalOutcome } from './proposalOutcome'
import { pollProposalWorkStatus, publishWorkStatus } from './proposalWorkStatus'

/**
 * Drive one confirmation card.
 *
 * Two things the obvious version gets wrong:
 *
 * 1. **State is resolved from the server on mount.** A card rehydrated from
 *    message history says "pending" forever, so a reload would offer Approve
 *    for an action already sent. The row is authoritative; this is not.
 * 2. **The outcome is reported, not assumed.** Approving returns the settled
 *    row, which may be `failed` — showing "Approved" there would tell the user
 *    an email went out when it did not.
 *
 * The decision itself is sent to the server, which runs the *stored* arguments.
 * It is deliberately not a chat message: the previous version posted
 * "Approved: …" into the conversation and let the model decide what that meant.
 */
export function useProposalDecision(proposalId, initialDecision) {
  const [state, setState] = useState(() => proposalOutcome({ status: initialDecision }))
  const [pending, setPending] = useState(false)

  useEffect(() => {
    if (!proposalId) return undefined
    const controller = new AbortController()
    fetchProposal({ proposalId, signal: controller.signal })
      .then((row) => {
        publishWorkStatus(row.workStatus)
        setState((current) => mergeProposalOutcome(current, proposalOutcome(row)))
      })
      .catch(() => {
        setState(proposalOutcome({
          status: 'status_unavailable',
          error: 'Unable to confirm this action status. Refresh to try again.',
        }))
      })
    return () => controller.abort()
  }, [proposalId])

  const decide = async (approved) => {
    if (pending || !state.resolved || state.status !== 'pending') return
    setPending(true)
    // Approval execution is synchronous from the browser's perspective, but
    // the server marks the immutable proposal `executing` before calling the
    // provider. Poll that authoritative row while the write is in flight so
    // Thoughts moves from Waiting to Sending without model involvement.
    const timer = approved
      ? pollProposalWorkStatus(fetchProposal, proposalId, (row) => {
        setState((current) => mergeProposalOutcome(current, proposalOutcome(row)))
      })
      : null
    try {
      const row = await decideProposal({ proposalId, approved })
      publishWorkStatus(row.work_status)
      setState((current) => mergeProposalOutcome(current, proposalOutcome(row)))
    } catch (caught) {
      try {
        const row = await fetchProposal({ proposalId })
        publishWorkStatus(row.workStatus)
        setState((current) => mergeProposalOutcome(current, proposalOutcome(row)))
      } catch {
        setState(proposalOutcome({
          status: 'status_unavailable',
          error: caught instanceof ProposalSettledError
            ? caught.message
            : 'Unable to confirm this action status. Refresh to try again.',
        }))
      }
    } finally {
      if (timer) window.clearInterval(timer)
      setPending(false)
    }
  }

  return { ...state, pending, checking: !state.resolved, decide }
}
